import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { fetchSuppliers } from "../../core/redux/slices/supplierSlice";
import { getPurchaseReport } from "../../core/api/reportService";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const DEFAULT_FILTERS = { from: today(), to: today(), supplierId: "" };
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const PurchaseReport = () => {
  const dispatch = useDispatch();
  const suppliers = useSelector((s) => s.suppliers.list);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const supplierOptions = [{ value: "", label: "All Suppliers" }, ...suppliers.map((s) => ({ value: s.id, label: s.supplierName }))];

  useEffect(() => { dispatch(fetchSuppliers()); }, [dispatch]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runReport(); }, []);

  const runReport = async (f = filters) => {
    setLoading(true);
    try {
      const res = await getPurchaseReport(f);
      const payload = res.data || {};
      setData(payload.records || payload || []);
      setSummary(payload.summary || null);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load report."), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    runReport(DEFAULT_FILTERS);
  };

  const columns = [
    { title: "PO / Ref", dataIndex: "purchaseNo", width: 150 },
    { title: "Date", dataIndex: "purchaseDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "Supplier", dataIndex: "supplierName" },
    { title: "Amount", dataIndex: "totalAmount", align: "right", width: 130, render: fmt, sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0) },
    { title: "Paid", dataIndex: "paidAmount", align: "right", width: 110, render: (v) => <span className="text-success">{fmt(v)}</span> },
    { title: "Balance", dataIndex: "balanceAmount", align: "right", width: 110, render: (v) => <span className={v > 0 ? "text-danger" : "text-success"}>{fmt(v)}</span> },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Purchase Report</h4><h6>Purchase-wise summary by supplier and date</h6></div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
              </div>
              <div className="col" style={{ minWidth: 200, maxWidth: 260 }}>
                <label className="form-label small mb-1">Supplier</label>
                <Select classNamePrefix="react-select" options={supplierOptions}
                  value={supplierOptions.find((o) => o.value && o.value === filters.supplierId) || null}
                  placeholder="All Suppliers"
                  isClearable
                  onChange={(opt) => setFilters((f) => ({ ...f, supplierId: opt?.value || "" }))} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={() => runReport()} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-dark" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {summary && (
          <div className="row mb-3">
            {[
              { label: "Total Purchases", value: fmt(summary.totalAmount), color: "primary" },
              { label: "Amount Paid", value: fmt(summary.paidAmount), color: "success" },
              { label: "Outstanding", value: fmt(summary.balanceAmount), color: "danger" },
              { label: "Orders", value: summary.count, color: "info" },
            ].map((s) => (
              <div key={s.label} className="col-6 col-md-3 mb-2">
                <div className={`card border-${s.color} text-center py-2`}>
                  <div className={`text-${s.color} fs-6 fw-bold`}>{s.value}</div>
                  <div className="small text-muted">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseReport;
